import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SectionLabel } from './DashboardWrapper';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Flex,
  useColorModeValue,
  Icon,
  Badge,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Progress,
  Link,
  Avatar,
  AvatarGroup,
} from '@chakra-ui/react';
import { useUser } from '../../contexts/UserContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
  ReferenceLine,
  PieChart,
  Pie,
  AreaChart,
  Area,
} from 'recharts';
import {
  FaChartLine,
  FaArrowUp,
  FaSearch,
  FaStar,
  FaUsers,
  FaMapMarkerAlt,
  FaPhone,
  FaGlobeAmericas,
  FaTrophy,
  FaAward,
  FaMedal,
  FaFireAlt,
} from 'react-icons/fa';
import RankingListComponent from '../RankingListComponent';
import ModernDetailsTable from '../ModernDetailsTable';
import BusinessReviewModal from '../BusinessReviewModal';
import MetricCard from '../MetricCard';
import BusinessBubbleChart from '../BusinessBubbleChart';
import { getYearData } from '../../services/yearService';
import { getBusinessImages } from '../../services/imageService';
import { filterMonthsByDateRange } from '../../utils/dateFiltering';
import { logger } from '../../utils/logger';
import { createFavoriteStarPieLabelRenderer } from '../FavoriteStarIndicator';
import { getTrafficValue, sortBusinessesBySeoScore } from '../../utils/competitiveIntelHelpers';

const GrossGrowthDashboard = ({ reviewsData, dateRange, demoMode, demoData }) => {
  // Set up color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  const [yearData, setYearData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [yearDataFetched, setYearDataFetched] = useState(false);
  const [businessImages, setBusinessImages] = useState({});

  // Colors for charts - using consistent color palette with more distinct colors
  const colors = useMemo(() => [
    '#2F80ED', // bright blue
    '#27AE60', // emerald green
    '#EB5757', // bright red
    '#9B51E0', // purple
    '#F2994A', // orange
    '#219EBC', // teal
    '#F264A6', // pink
    '#BB6BD9', // violet
    '#2D9CDB', // light blue
    '#219653', // dark green
    '#F2C94C', // yellow
    '#6FCF97', // light green
    '#56CCF2', // sky blue
    '#F2994A', // peach
    '#9B51E0', // lavender
    '#EB5757', // coral
    '#2F80ED', // royal blue
    '#27AE60', // forest green
    '#BB6BD9', // orchid
    '#F264A6'  // rose
  ], []);
  const [businessColors, setBusinessColors] = useState({});

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          {/* Shadow layer for depth */}
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          {/* Base dark gold layer */}
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          {/* Bright gold overlay */}
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          {/* Top highlight for 3D shine */}
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Custom label renderer for pie charts that shows spinning stars for favorited businesses
  const FavoriteStarPieLabel = useMemo(() => {
    return createFavoriteStarPieLabelRenderer({
      businessNameToUriMap,
      favoriteUris
    });
  }, [businessNameToUriMap, favoriteUris]);

  // Effect to fetch yearData once
  useEffect(() => {
    if (demoMode && demoData) {
      setYearData(demoData.yearData || []);
      setCombinedData(demoData.combinedData || []);
      setIsLoading(false);
      return;
    }
    const fetchInitialYearData = async () => {
      // console.log("Attempting to fetch year data...");
      setIsLoading(true); // Set loading true before starting year data fetch
      try {
        const yearDataResponse = await getYearData();
        setYearData(yearDataResponse);
        setYearDataFetched(true);
        // console.log("Year data fetched successfully.");
      } catch (error) {
        console.error("Error fetching year data:", error);
        setYearDataFetched(true); // Mark as fetched even on error to avoid repeated attempts
      } finally {
        //isLoading will be set to false in the processing useEffect
      }
    };

    if (!yearDataFetched) {
      fetchInitialYearData();
    }
  }, [yearDataFetched]); // Runs if yearDataFetched changes (initially false)

  // Effect to combine data and set final loading state
  useEffect(() => {
    // console.log("Combine data effect triggered. reviewsData length:", reviewsData?.length, "yearDataFetched:", yearDataFetched);
    if (reviewsData?.length > 0 && yearDataFetched) {
      if (!isLoading) setIsLoading(true); // Ensure loading is true for processing
      // console.log("Processing combined data...");
        const combined = reviewsData.map(currentData => {
        const historicalData = yearData.find(yd =>
            yd.maps_uri === currentData.maps_uri
          );
          return {
            ...currentData,
            historicalData: historicalData?.data || null
          };
        });
        setCombinedData(combined);
        setIsLoading(false);
      // console.log("Combined data processed, isLoading set to false.");
    } else if (yearDataFetched) { // Handles case where reviewsData is empty but yearData fetch attempt is complete
      setCombinedData([]);
      setIsLoading(false);
      // console.log("No reviewsData, or yearData not fetched properly. isLoading set to false.");
    }
    // If yearDataFetched is false, we are waiting for the other effect, so isLoading should remain true or be handled by initial spinner logic.
  }, [reviewsData, yearData, dateRange, yearDataFetched, isLoading]); // Added isLoading to deps to allow setting it true if needed

  useEffect(() => {
    // Assign colors to businesses
    if (reviewsData) {
      const newBusinessColors = {};
      reviewsData.forEach((business, index) => {
        newBusinessColors[business.name] = colors[index % colors.length];
      });
      setBusinessColors(newBusinessColors);
    }
  }, [reviewsData, colors]);

  // Fetch business images for podium
  useEffect(() => {
    if (demoMode) return;
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        // Don't block UI on image fetch failure
        console.error('Error fetching business images:', error);
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  const getColor = useCallback((businessName) => {
    return businessColors[businessName] || colors[0];
  }, [businessColors, colors]);

  // Calculate growth metrics for each business based on selected date range
  const businessGrowthData = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return [];
    }

    return combinedData.map(business => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};

      // Filter months based on selected date range using standardized utility
      let months = Object.keys(monthlyData).sort();
      const originalMonthCount = months.length;

      if (dateRange?.startDate && dateRange?.endDate) {
        months = filterMonthsByDateRange(months, dateRange);
        
        // Debug logging
        logger.log(`GrossGrowth - Business: ${business.name || 'Unknown'}`);
        logger.log(`Original months: ${originalMonthCount}, Filtered: ${months.length}`);
        logger.log(`Filtered months: ${months.join(', ')}`);
      }

      // Skip if not enough data
      if (months.length === 0) {
        return {
          ...business,
          threeMonthGrowth: 0,
          monthlyGrowth: [0, 0, 0],
          growthRate: 0,
          totalGrowth: 0,
          totalPeriodGrowth: 0,
          periodsAvailable: 0,
          totalReviews: business.rating_count || 0
        };
      }

      // Calculate growth based on ALL filtered months (not just last 3)
      const totalPeriodGrowth = months.reduce((sum, month) => sum + monthlyData[month], 0);

      // Calculate average monthly growth rate for the ENTIRE selected period
      const growthRate = months.length > 0 ? totalPeriodGrowth / months.length : 0;

      // Still calculate 3-month growth for comparison, but use it as secondary metric
      const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
      const threeMonthGrowth = lastThreeMonths.reduce((sum, month) => sum + monthlyData[month], 0);
      const monthlyGrowth = lastThreeMonths.map(month => monthlyData[month]);

      // Debug logging to see the difference
      logger.log(`📊 Growth Calc - ${business.name}:`);
      logger.log(`  Filtered months: [${months.join(', ')}] (${months.length})`);
      logger.log(`  Total period growth: ${totalPeriodGrowth}`);
      logger.log(`  Last 3 months growth: ${threeMonthGrowth}`);
      logger.log(`  Period avg: ${growthRate.toFixed(1)}/month`);

      return {
        ...business,
        threeMonthGrowth,
        monthlyGrowth,
        growthRate,
        totalPeriodGrowth,
        totalGrowth: totalPeriodGrowth, // Use full period growth as main metric
        periodsAvailable: months.length,
        totalReviews: business.rating_count
      };
    });
  }, [combinedData, dateRange]);

  // Sort businesses by growth (now using total period growth instead of just 3 months)
  const sortedByGrowth = useMemo(() => {
    if (!businessGrowthData.length) return [];
    return [...businessGrowthData].sort((a, b) => b.totalGrowth - a.totalGrowth);
  }, [businessGrowthData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  const top5Growth = useMemo(() => {
    if (!sortedByGrowth.length) return [];
    return sortedByGrowth.slice(0, 5);
  }, [sortedByGrowth]);

  const bottom5Growth = useMemo(() => {
    if (!sortedByGrowth.length) return [];
    return sortedByGrowth.slice(-5).reverse();
  }, [sortedByGrowth]);

  const businessSentimentData = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData.map(business => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const total = sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative;
      const sentimentScore = total > 0 
        ? ((sentimentBreakdown.positive + (sentimentBreakdown.neutral * 0.5)) / total * 100)
        : 0;
      return { ...business, sentimentScore, ...sentimentBreakdown, total };
    });
  }, [combinedData]);

  // Sort businesses based on sentiment score
  const sortedBySentiment = useMemo(() => {
    if (!businessSentimentData.length) return [];
    return [...businessSentimentData].sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [businessSentimentData]);

  // Sort businesses by total reviews
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  // Calculate market-wide metrics
  const marketMetrics = useMemo(() => {
    if (!sortedByGrowth.length) {
      return {
        totalMarketGrowth: 0,
        averageGrowth: 0,
        medianGrowth: 0,
        avgMonthlyGrowth: 0
      };
    }

    const totalMarketGrowth = sortedByGrowth.reduce((sum, b) => sum + (b?.totalGrowth || 0), 0);
    const averageGrowth = sortedByGrowth.length > 0 ? totalMarketGrowth / sortedByGrowth.length : 0;
    const medianGrowth = sortedByGrowth.length > 0 ?
      sortedByGrowth[Math.floor(sortedByGrowth.length / 2)]?.totalGrowth || 0 : 0;
    const avgMonthlyGrowth = averageGrowth / 3;

    return {
      totalMarketGrowth,
      averageGrowth,
      medianGrowth,
      avgMonthlyGrowth
    };
  }, [sortedByGrowth]);

  // Prepare data for bar chart
  const barChartData = useMemo(() => {
    if (!sortedByGrowth.length) return [];

    return sortedByGrowth.slice(0, 20).map(business => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      growth: business.totalGrowth,
      fill: getColor(business.name)
    }));
  }, [sortedByGrowth, getColor]);

  // Prepare data for pie chart
  const pieChartData = useMemo(() => {
    if (!sortedByGrowth.length) return [];

    return sortedByGrowth.slice(0, 10)
      .filter(business => business && business.totalGrowth !== undefined)
      .map((business, index) => ({
        name: business.name.split(' ').slice(0, 2).join(' '),
        value: business.totalGrowth,
        fill: getColor(business.name)
      }));
  }, [sortedByGrowth, getColor]);

  // Prepare monthly trend data for top 5 businesses based on date range
  const monthlyTrendData = useMemo(() => {
    if (!top5Growth.length || !sortedByGrowth.length) return [];

    const months = Object.keys(top5Growth[0]?.historicalData?.monthlyReviewCounts || {}).sort();
    if (!months || months.length === 0) return [];

    // Filter months based on selected date range using standardized utility
    let filteredMonths = [...months];
    if (dateRange?.startDate && dateRange?.endDate) {
      filteredMonths = filterMonthsByDateRange(months, dateRange);
      
      // Debug logging for trend data
      logger.log(`MonthlyTrend - Original months: ${months.length}, Filtered: ${filteredMonths.length}`);
      logger.log(`Trend months: ${filteredMonths.join(', ')}`);
    }

    // Use all available months or limit to the last 6 if there are more
    const displayMonths = filteredMonths.length > 6 ?
      filteredMonths.slice(-6) : filteredMonths;

    return displayMonths.map(month => {
      const monthData = {
        month: month.split('-')[1], // Get just the month part
        year: month.split('-')[0], // Get the year part
        fullMonth: month // Keep the full date for sorting and display
      };

      // Calculate market average for this month
      const monthTotal = sortedByGrowth.reduce((sum, business) => {
        const monthlyData = business.historicalData?.monthlyReviewCounts || {};
        return sum + (monthlyData[month] || 0);
      }, 0);
      monthData['Market Average'] = monthTotal / sortedByGrowth.length;

      // Add data for top 5 businesses
      top5Growth.forEach(business => {
        if (business) {
          const monthlyData = business.historicalData?.monthlyReviewCounts || {};
          monthData[business.name.split(' ').slice(0, 2).join(' ')] = monthlyData[month] || 0;
        }
      });

      return monthData;
    }).sort((a, b) => new Date(a.fullMonth) - new Date(b.fullMonth)); // Ensure chronological order
  }, [top5Growth, sortedByGrowth, dateRange]);

  // Prepare data for area chart
  const areaChartData = useMemo(() => {
    if (!monthlyTrendData.length) return [];

    return monthlyTrendData.map(item => ({
      month: `${item.month}-${item.year.slice(2)}`,
      ...item
    }));
  }, [monthlyTrendData]);

  // Prepare data for all businesses line chart (Review Growth Over Time)
  const allBusinessesGrowthData = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate || !combinedData.length)
      return [];

    // Get all unique months from all businesses
    const allMonths = new Set();
    combinedData.forEach((business) => {
      if (business.historicalData?.monthlyReviewCounts) {
        Object.keys(business.historicalData.monthlyReviewCounts).forEach((month) => {
          const monthMoment = require('moment')(month);
          if (monthMoment.isBetween(dateRange.startDate, dateRange.endDate, 'month', '[]')) {
            allMonths.add(month);
          }
        });
      }
    });

    // Sort months chronologically
    const sortedMonths = Array.from(allMonths).sort();

    // Create data points for each month with monthly reviews for each business
    const lineData = sortedMonths.map((month) => {
      const moment = require('moment');
      const dataPoint = {
        month,
        displayMonth: moment(month).format('MMM YY')
      };

      // Add each business's review count for this month
      combinedData.forEach((business) => {
        const monthlyData = business.historicalData?.monthlyReviewCounts || {};
        const shortName = business.name.split(' ').slice(0, 2).join(' ');
        dataPoint[shortName] = monthlyData[month] || 0;
      });

      return dataPoint;
    });

    return lineData;
  }, [combinedData, dateRange]);

  // Get business names for the line chart legend
  const allBusinessNames = useMemo(() => {
    if (!combinedData.length) return [];
    return combinedData
      .sort((a, b) => (b.totalGrowth || b.rating_count || 0) - (a.totalGrowth || a.rating_count || 0))
      .map((business) => business.name.split(' ').slice(0, 2).join(' '));
  }, [combinedData]);

  // Add histogram utility function
  const createHistogramBuckets = useCallback((data, valueKey, bucketCount = 10) => {
    if (!data || data.length === 0) return [];
    
    const values = data.map(item => item[valueKey]).filter(val => val !== undefined && val !== null);
    if (values.length === 0) return [];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const bucketSize = (max - min) / bucketCount || 1;

    const buckets = Array.from({ length: bucketCount }, (_, i) => ({
      range: `${Math.round(min + i * bucketSize)}-${Math.round(min + (i + 1) * bucketSize)}`,
      min: min + i * bucketSize,
      max: min + (i + 1) * bucketSize,
      count: 0,
      businesses: []
    }));
    
    // Fill buckets
    data.forEach(item => {
      const value = item[valueKey];
      if (value !== undefined && value !== null) {
        const bucketIndex = Math.min(Math.max(Math.floor((value - min) / bucketSize), 0), bucketCount - 1);
        buckets[bucketIndex].count++;
        buckets[bucketIndex].businesses.push(item);
      }
    });
    
    return buckets;
  }, []);

  // Create histogram data for growth metrics
  const growthHistogram = useMemo(() => {
    return createHistogramBuckets(businessGrowthData, 'threeMonthGrowth', 16);
  }, [businessGrowthData, createHistogramBuckets]);

  const growthRateHistogram = useMemo(() => {
    return createHistogramBuckets(businessGrowthData, 'growthRate', 16);
  }, [businessGrowthData, createHistogramBuckets]);

  // Add early return for loading state
  if (isLoading || !reviewsData || reviewsData.length === 0) {
    return (
      <Flex justify="center" align="center" minH="300px" direction="column">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
        <Text mt={4} fontSize="lg" color="gray.500">Loading growth data...</Text>
      </Flex>
    );
  }

  // Prepare data table columns
  const columns = [
    {
      id: 1,
      name: 'Business Name',
      selector: row => row.name,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <Text fontWeight="semibold" fontSize="md" noOfLines={1}>{row.name}</Text>
      ),
    },
    {
      id: 2,
      name: 'Period Growth',
      selector: row => row.totalGrowth,
      sortable: true,
      right: true,
      cell: row => (
        <HStack spacing={2} justify="flex-end">
          <Text fontWeight="semibold" fontFeatureSettings="'tnum' 1">
            +{row.totalGrowth.toLocaleString()}
          </Text>
          {row.totalGrowth > marketMetrics.averageGrowth && (
            <Badge colorScheme="green" size="sm" borderRadius="full">
              <Icon as={FaArrowUp} boxSize={2} mr={1} />
              Above Avg
            </Badge>
          )}
        </HStack>
      ),
    },
    {
      id: 3,
      name: 'Monthly Average',
      selector: row => row.growthRate,
      sortable: true,
      right: true,
      cell: row => (
        <Badge 
          colorScheme={row.growthRate > marketMetrics.avgMonthlyGrowth ? 'green' : 'gray'} 
          borderRadius="full" 
          px={2}
        >
          {row.growthRate.toFixed(1)}/mo
        </Badge>
      ),
    },
    {
      id: 4,
      name: 'Total Reviews',
      selector: row => row.totalReviews,
      sortable: true,
      right: true,
      cell: row => (
        <Text fontWeight="medium" fontFeatureSettings="'tnum' 1">
          {row.totalReviews.toLocaleString()}
        </Text>
      ),
    }
  ];

  const handleBusinessClick = (business) => {
     const businessWithFullData = combinedData.find(cb => cb.maps_uri === business.maps_uri);
    setSelectedBusiness(businessWithFullData);
    setIsModalOpen(true);
  };

  // Business Growth Card component
  const BusinessGrowthCard = ({ business }) => {
    const growthColor = business.totalGrowth > marketMetrics.averageGrowth
      ? "green.500"
      : business.totalGrowth > 0
        ? "blue.500"
        : "gray.500";

    // Icon selection based on growth performance
    // Not currently used but kept for future UI enhancement

    // Define hover background color at component level
    const hoverBg = useColorModeValue('gray.50', 'gray.700');

    // Process monthly data for mini chart
    const monthlyData = useMemo(() => {
      return Object.entries(business.historicalData?.monthlyReviewCounts || {})
        .map(([month, count]) => ({
          month: month,
          count: count
        }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);  // Get last 6 months
    }, [business.historicalData]);

    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        p={4}
        cursor="pointer"
        onClick={() => handleBusinessClick(business)}
        transition="all 0.2s"
        _hover={{
          transform: 'translateY(-4px)',
          shadow: 'lg',
          borderColor: 'blue.400',
          bg: hoverBg
        }}
        height="100%"
      >
        <VStack align="stretch" spacing={4} height="100%">
          <Box flex="1">
            <Heading
              size="md"
              mb={2}
              lineHeight="1.4"
              minHeight="2.8em"
              overflow="hidden"
              css={{
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
              title={business.name}
            >
              {business.name}
            </Heading>
            <HStack spacing={2} mb={2} justify="space-between">
              <Badge colorScheme="blue">
                {business.primary_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Business'}
              </Badge>
              <HStack spacing={1}>
                <Icon as={FaStar} color="yellow.400" boxSize={4} />
                <Text as='span' fontWeight="bold">{business.rating_avg?.toFixed(1) || '0.0'}</Text>
                <Text as='span' color="gray.500">({parseInt(business.rating_count || 0).toLocaleString()})</Text>
              </HStack>
            </HStack>
          </Box>

          {/* Growth Metrics */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1}>Growth Metrics</Text>

            {/* Mini Area Chart */}
            <Box h="80px" mb={2}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id={`colorGrowth-${business.name.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={growthColor} stopOpacity={0.4} />
                      <stop offset="50%" stopColor={growthColor} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={growthColor} stopOpacity={0.05} />
                    </linearGradient>
                    <filter id={`glow-${business.name.replace(/\s+/g, '')}`} x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  <Area
                    type="natural"
                    dataKey="count"
                    stroke={growthColor}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fillOpacity={1}
                    fill={`url(#colorGrowth-${business.name.replace(/\s+/g, '')})`}
                    filter={`url(#glow-${business.name.replace(/\s+/g, '')})`}
                    isAnimationActive={true}
                    animationDuration={1200}
                    animationEasing="ease-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={3}>
              <Stat bg={statBg} p={2} borderRadius="md">
                <StatLabel fontSize="xs">Period Growth</StatLabel>
                <StatNumber fontSize="lg" color={growthColor}>
                  {(business.totalGrowth || 0).toLocaleString()}
                </StatNumber>
              </Stat>
              <Stat bg={statBg} p={2} borderRadius="md">
                <StatLabel fontSize="xs">Monthly Avg</StatLabel>
                <StatNumber fontSize="lg" color="blue.500">
                  {(business.growthRate || 0).toFixed(1)}
                </StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>

          {/* Basic Info */}
          <VStack align="start" spacing={1} mt="auto" fontSize="sm">
            <HStack spacing={2}>
              <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={4} />
              <Text as='span' noOfLines={1}>{business.address || 'No address available'}</Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FaUsers} color="gray.500" boxSize={4} />
              <Text as='span'>{business.rating_count} total reviews</Text>
            </HStack>
          </VStack>
        </VStack>
      </Box>
    );
  };

  return (
    <Box>

      {/* Top 3 Podium - Only show on desktop and tablet */}
      <Box
        display={{ base: "none", md: "block" }}
        bg="transparent"
        mb={8}
      >
        <Flex justify="center" align="flex-end" gap={6} w="full">
          {/* Silver - Second Place */}
          <Box
            bg={cardBg}
            borderRadius="2xl"
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => top5Growth[1] && handleBusinessClick(top5Growth[1])}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              transform: 'translateY(-8px)', 
              shadow: '2xl',
            }}
            overflow="hidden"
            flex="1"
            maxW="400px"
            boxShadow="xl"
            borderWidth="1px"
            borderColor={borderColor}
          >
            {/* Gradient accent bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, gray.300, gray.500, gray.300)"
            />
            <VStack p={6} spacing={4}>
              {/* Rank badge */}
              <Flex
                position="absolute"
                top={4}
                left={4}
                minW="36px"
                h="36px"
                px={2}
                borderRadius="full"
                bgGradient="linear(to-br, gray.300, gray.500)"
                justify="center"
                align="center"
                shadow="md"
              >
                <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">2</Text>
              </Flex>
              
              <Icon as={FaMedal} color="gray.400" boxSize={6} />
              
              {/* Avatar with ring */}
              <Box
                p="3px"
                borderRadius="full"
                bgGradient="linear(to-br, gray.300, gray.500)"
              >
                <Avatar 
                  name={top5Growth[1]?.name} 
                  src={businessImages[top5Growth[1]?.maps_uri] || ''} 
                  bg="gray.100" 
                  boxSize="70px"
                  borderWidth="3px"
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Heading size="sm" noOfLines={1} color="gray.700">{top5Growth[1]?.name}</Heading>
                <Text 
                  fontWeight="extrabold" 
                  fontSize="2xl" 
                  bgGradient="linear(to-r, gray.600, gray.800)"
                  bgClip="text"
                >
                  +{(top5Growth[1]?.totalGrowth || 0).toLocaleString()}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                  new reviews
                </Text>
              </VStack>
              
              {/* Progress indicator */}
              <Box w="full" px={2}>
                <Box
                  h="6px"
                  bg="gray.100"
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    w={`${(top5Growth[1]?.totalGrowth / (top5Growth[0]?.totalGrowth || 1)) * 100}%`}
                    bgGradient="linear(to-r, gray.400, gray.500)"
                    borderRadius="full"
                    transition="width 0.5s ease-out"
                  />
                </Box>
              </Box>
            </VStack>
          </Box>

          {/* Gold - First Place */}
          <Box
            bg={cardBg}
            borderRadius="2xl"
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => top5Growth[0] && handleBusinessClick(top5Growth[0])}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              transform: 'translateY(-12px) scale(1.02)', 
              shadow: '0 25px 50px -12px rgba(251, 191, 36, 0.4)',
            }}
            overflow="hidden"
            flex="1.15"
            maxW="450px"
            boxShadow="0 20px 40px -12px rgba(251, 191, 36, 0.3)"
            zIndex={2}
            mb={4}
            borderWidth="1px"
            borderColor="yellow.200"
          >
            {/* Animated gradient accent bar */}
            <Box
              h="6px"
              bgGradient="linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)"
              backgroundSize="200% 100%"
              animation="shimmer 2s linear infinite"
              sx={{
                '@keyframes shimmer': {
                  '0%': { backgroundPosition: '200% 0' },
                  '100%': { backgroundPosition: '-200% 0' },
                },
              }}
            />
            <VStack p={8} spacing={4}>
              {/* Crown/Rank badge */}
              <Flex
                position="absolute"
                top={4}
                left={4}
                minW="40px"
                h="40px"
                px={2}
                borderRadius="full"
                bgGradient="linear(to-br, yellow.300, orange.400)"
                justify="center"
                align="center"
                shadow="lg"
              >
                <Text color="white" fontWeight="bold" fontSize="lg" lineHeight="1">1</Text>
              </Flex>
              
              <Icon as={FaTrophy} color="yellow.400" boxSize={10} filter="drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))" />
              
              {/* Avatar with golden ring */}
              <Box
                p="4px"
                borderRadius="full"
                bgGradient="linear(to-br, yellow.300, orange.400, yellow.500)"
                shadow="lg"
              >
                <Avatar 
                  name={top5Growth[0]?.name} 
                  src={businessImages[top5Growth[0]?.maps_uri] || ''} 
                  bg="yellow.50" 
                  boxSize="90px"
                  borderWidth="4px"
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Heading size="md" noOfLines={1} color="gray.800">{top5Growth[0]?.name}</Heading>
                <Text 
                  fontWeight="extrabold" 
                  fontSize="3xl" 
                  bgGradient="linear(to-r, yellow.500, orange.500)"
                  bgClip="text"
                >
                  +{(top5Growth[0]?.totalGrowth || 0).toLocaleString()}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                  new reviews
                </Text>
              </VStack>
              
              {/* Progress indicator */}
              <Box w="full" px={2}>
                <Box
                  h="8px"
                  bg="yellow.50"
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    w="100%"
                    bgGradient="linear(to-r, yellow.400, orange.400)"
                    borderRadius="full"
                  />
                </Box>
              </Box>
            </VStack>
          </Box>

          {/* Bronze - Third Place */}
          <Box
            bg={cardBg}
            borderRadius="2xl"
            textAlign="center"
            position="relative"
            cursor="pointer"
            onClick={() => top5Growth[2] && handleBusinessClick(top5Growth[2])}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              transform: 'translateY(-8px)', 
              shadow: '2xl',
            }}
            overflow="hidden"
            flex="1"
            maxW="400px"
            boxShadow="xl"
            borderWidth="1px"
            borderColor={borderColor}
          >
            {/* Gradient accent bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, orange.200, orange.400, orange.200)"
            />
            <VStack p={6} spacing={4}>
              {/* Rank badge */}
              <Flex
                position="absolute"
                top={4}
                left={4}
                minW="36px"
                h="36px"
                px={2}
                borderRadius="full"
                bgGradient="linear(to-br, orange.300, orange.500)"
                justify="center"
                align="center"
                shadow="md"
              >
                <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">3</Text>
              </Flex>
              
              <Icon as={FaAward} color="orange.400" boxSize={6} />
              
              {/* Avatar with ring */}
              <Box
                p="3px"
                borderRadius="full"
                bgGradient="linear(to-br, orange.300, orange.500)"
              >
                <Avatar 
                  name={top5Growth[2]?.name} 
                  src={businessImages[top5Growth[2]?.maps_uri] || ''} 
                  bg="orange.50" 
                  boxSize="70px"
                  borderWidth="3px"
                  borderColor={cardBg}
                />
              </Box>
              
              <VStack spacing={1}>
                <Heading size="sm" noOfLines={1} color="gray.700">{top5Growth[2]?.name}</Heading>
                <Text 
                  fontWeight="extrabold" 
                  fontSize="2xl" 
                  bgGradient="linear(to-r, orange.500, orange.600)"
                  bgClip="text"
                >
                  +{(top5Growth[2]?.totalGrowth || 0).toLocaleString()}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                  new reviews
                </Text>
              </VStack>
              
              {/* Progress indicator */}
              <Box w="full" px={2}>
                <Box
                  h="6px"
                  bg="orange.50"
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    w={`${(top5Growth[2]?.totalGrowth / (top5Growth[0]?.totalGrowth || 1)) * 100}%`}
                    bgGradient="linear(to-r, orange.300, orange.500)"
                    borderRadius="full"
                    transition="width 0.5s ease-out"
                  />
                </Box>
              </Box>
            </VStack>
          </Box>
        </Flex>
      </Box>

            {/* Section Label */}
            {!demoMode && <SectionLabel description="Monitor how quickly you're gaining new reviews over time. Consistent growth signals an active, engaged customer base.">Review Growth</SectionLabel>}

{/* Histogram Section */}


      {/* Main Content Area with Tabs */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        shadow="md"
        borderWidth="1px"
        borderColor={borderColor}
        mb={8}
        overflow="hidden"
      >
        <Tabs>
          <TabList px={4} pt={4}>
            <Tab fontWeight="medium">Overview</Tab>
            <Tab fontWeight="medium">Businesses</Tab>
            <Tab fontWeight="medium">Trends</Tab>
            <Tab fontWeight="medium">Histogram</Tab>
            <Tab fontWeight="medium">Rankings</Tab>
            <Tab fontWeight="medium">Details</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Panel */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">
                {/* Growth Distribution Bar Chart */}
                <Box height={{ base: "350px", md: "600px" }}>
                  <Heading size="md" mb={4} color="gray.700">Growth Distribution</Heading>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barChartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                      animationDuration={1000}
                      animationBegin={0}
                      animationEasing="ease-out"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                      />
                      <YAxis tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }} domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                      <RechartsTooltip
                        formatter={(value) => value.toLocaleString()}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box
                                bg="white"
                                p={3}
                                boxShadow="lg"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                              >
                                <Text fontWeight="bold">{payload[0].payload.name}</Text>
                                <Text>
                                  {payload[0].value.toLocaleString()} new reviews
                                </Text>
                                <Text color="orange.500">
                                  Market Average: {marketMetrics.averageGrowth.toLocaleString()}
                                </Text>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="growth"
                        fill={colors[0]}
                        cursor="pointer"
                        onClick={(data) => {
                          const business = sortedByGrowth.find(b =>
                            b.name.split(' ').slice(0, 2).join(' ') === data.name
                          );
                          if (business) handleBusinessClick(business);
                        }}
                        isAnimationActive={true}
                        animationDuration={1000}
                        animationBegin={300}
                        animationEasing="ease-out"
                        label={FavoriteStarBarLabel}
                      >
                        {barChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index < 5 ? entry.fill : '#CBD5E0'}
                          />
                        ))}
                      </Bar>
                      <ReferenceLine
                        y={marketMetrics.averageGrowth}
                        stroke="#ED8936"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        label={{
                          value: 'Market Average',
                          position: 'right',
                          fill: '#ED8936'
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Growth Leaders Pie Chart */}
                <Box height={{ base: "350px", md: "600px" }} overflow="visible">
                  <Heading size="md" mb={4} color="gray.700">Market Distribution</Heading>
                  <ResponsiveContainer width="100%" height="100%" style={{ overflow: 'visible' }}>
                    <PieChart style={{ overflow: 'visible' }}>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={180}
                        paddingAngle={2}
                        dataKey="value"
                        label={FavoriteStarPieLabel}
                        labelLine={{ stroke: '#4A5568', strokeWidth: 1 }}
                        isAnimationActive={true}
                        animationBegin={300}
                        animationDuration={800}
                        animationEasing="ease-out"
                        cursor="pointer"
                        onClick={(data) => {
                          const business = sortedByGrowth.find(b =>
                            b.name.split(' ').slice(0, 2).join(' ') === data.name
                          );
                          if (business) handleBusinessClick(business);
                        }}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => value.toLocaleString()}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #E2E8F0',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Businesses Panel */}
            <TabPanel>
              <Box h={{ base: "350px", md: "600px" }}>
                <BusinessBubbleChart
                  businesses={sortedByGrowth}
                  valueKey="totalGrowth"
                  valueLabel="Review Growth"
                  onBusinessClick={handleBusinessClick}
                  formatValue={(val) => `+${val?.toLocaleString() || '0'}`}
                  colorScheme="orange"
                />
              </Box>
            </TabPanel>

            {/* Trends Panel */}
            <TabPanel>
              <Box height={{ base: "300px", md: "500px" }}>
                <Heading size="md" mb={4} color="gray.700">Monthly Growth Trends</Heading>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={areaChartData}>
                    <defs>
                      {top5Growth.map((business, index) => (
                        business && (
                          <linearGradient
                            key={business.name}
                            id={`color${index}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={getColor(business.name)}
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor={getColor(business.name)}
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                        )
                      ))}
                      <linearGradient id="colorMarket" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ED8936" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#ED8936" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                    />
                    <YAxis tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }} />
                    <RechartsTooltip
                      formatter={(value) => value.toLocaleString()}
                      labelFormatter={(value) => `Month: ${value}`}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Market Average"
                      stroke="#ED8936"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorMarket)"
                      strokeDasharray="5 5"
                    />
                    {top5Growth.map((business, index) => (
                      business && (
                        <Area
                          key={business.name}
                          type="monotone"
                          dataKey={business.name.split(' ').slice(0, 2).join(' ')}
                          stroke={getColor(business.name)}
                          strokeWidth={2}
                          fillOpacity={1}
                          fill={`url(#color${index})`}
                        />
                      )
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </TabPanel>

            {/* Enhanced Histogram Panel */}
            <TabPanel>
              <VStack spacing={8} align="stretch">
                {/* Header Section */}
                <Box textAlign="center" py={4}>
                  <Heading size="lg" mb={2} color="green.600">
                    📈 Growth Distribution Analysis
                  </Heading>
                  <Text fontSize="md" color="gray.600" maxW="600px" mx="auto">
                    Analyze how businesses are distributed across different growth ranges to identify high-performers and market opportunities.
                  </Text>
                </Box>

                {/* Growth Amount Histogram */}
                <Box 
                  bg={cardBg} 
                  borderRadius="xl" 
                  p={6} 
                  shadow="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <HStack justify="space-between" align="center" mb={6}>
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color="gray.700">Growth Amount Distribution</Heading>
                      <Text fontSize="sm" color="gray.500">
                        Distribution of {businessGrowthData.length} businesses by 3-month growth amounts
                      </Text>
                    </VStack>
                    <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                      {growthHistogram.filter(bucket => bucket.count > 0).length} Active Ranges
                    </Badge>
                  </HStack>
                  
                  <Box height={{ base: "280px", md: "450px" }} position="relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={growthHistogram}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <defs>
                          <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#27AE60" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#27AE60" stopOpacity={0.3}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="range" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          label={{ value: 'Growth Amount Ranges', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 } }}
                        />
                        <YAxis 
                          label={{ value: 'Number of Businesses', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 } }}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg="white"
                                  p={4}
                                  boxShadow="xl"
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="lg"
                                  maxW="350px"
                                >
                                  <VStack align="start" spacing={2}>
                                    <Text fontWeight="bold" fontSize="md" color="green.600">
                                      🚀 Growth Range: {data.range} reviews
                                    </Text>
                                    <Text fontSize="sm">
                                      <strong>{data.count}</strong> businesses in this range
                                    </Text>
                                    <Text fontSize="sm" color="gray.600">
                                      {((data.count / businessGrowthData.length) * 100).toFixed(1)}% of total market
                                    </Text>
                                    {data.businesses.length > 0 && (
                                      <>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700" mt={2}>
                                          Top Examples:
                                        </Text>
                                        {data.businesses.slice(0, 3).map((business, idx) => (
                                          <Text key={idx} fontSize="xs" color="gray.600">
                                            • {business.name} (+{business.threeMonthGrowth} reviews)
                                          </Text>
                                        ))}
                                        {data.businesses.length > 3 && (
                                          <Text fontSize="xs" color="green.500" fontStyle="italic">
                                            ... and {data.businesses.length - 3} more businesses
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          fill="url(#growthGradient)"
                          cursor="pointer"
                          onClick={(data) => {
                            if (data && data.businesses && data.businesses.length > 0) {
                              handleBusinessClick(data.businesses[0]);
                            }
                          }}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Growth Rate Histogram */}
                <Box 
                  bg={cardBg} 
                  borderRadius="xl" 
                  p={6} 
                  shadow="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <HStack justify="space-between" align="center" mb={6}>
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color="gray.700">Monthly Growth Rate Distribution</Heading>
                      <Text fontSize="sm" color="gray.500">
                        Distribution of businesses by average monthly growth rates
                      </Text>
                    </VStack>
                    <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                      {growthRateHistogram.filter(bucket => bucket.count > 0).length} Active Ranges
                    </Badge>
                  </HStack>
                  
                  <Box height={{ base: "280px", md: "450px" }} position="relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={growthRateHistogram}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <defs>
                          <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F2994A" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#F2994A" stopOpacity={0.3}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis 
                          dataKey="range" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          label={{ value: 'Monthly Growth Rate Ranges', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 } }}
                        />
                        <YAxis 
                          label={{ value: 'Number of Businesses', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#2D3748', fontWeight: 600 } }}
                          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
                          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg="white"
                                  p={4}
                                  boxShadow="xl"
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="lg"
                                  maxW="350px"
                                >
                                  <VStack align="start" spacing={2}>
                                    <Text fontWeight="bold" fontSize="md" color="orange.600">
                                      ⚡ Rate Range: {data.range} reviews/month
                                    </Text>
                                    <Text fontSize="sm">
                                      <strong>{data.count}</strong> businesses in this range
                                    </Text>
                                    <Text fontSize="sm" color="gray.600">
                                      {((data.count / businessGrowthData.length) * 100).toFixed(1)}% of total market
                                    </Text>
                                    {data.businesses.length > 0 && (
                                      <>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700" mt={2}>
                                          Top Examples:
                                        </Text>
                                        {data.businesses.slice(0, 3).map((business, idx) => (
                                          <Text key={idx} fontSize="xs" color="gray.600">
                                            • {business.name} ({business.growthRate.toFixed(1)}/month)
                                          </Text>
                                        ))}
                                        {data.businesses.length > 3 && (
                                          <Text fontSize="xs" color="orange.500" fontStyle="italic">
                                            ... and {data.businesses.length - 3} more businesses
                                          </Text>
                                        )}
                                      </>
                                    )}
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          fill="url(#rateGradient)"
                          cursor="pointer"
                          onClick={(data) => {
                            if (data && data.businesses && data.businesses.length > 0) {
                              handleBusinessClick(data.businesses[0]);
                            }
                          }}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Enhanced Statistics Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                  <Box>
                    <Heading size="sm" mb={4} color="green.600">📊 Growth Amount Statistics</Heading>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                      <Stat 
                        bg="gradient-to-br from-green-50 to-green-100" 
                        p={4} 
                        borderRadius="xl" 
                        shadow="md"
                        borderWidth="1px"
                        borderColor="green.200"
                      >
                        <StatLabel fontSize="sm" color="green.600" fontWeight="medium">
                          🏆 Most Common Range
                        </StatLabel>
                        <StatNumber fontSize="lg" color="green.700">
                          {growthHistogram.length > 0 
                            ? growthHistogram.reduce((max, bucket) => 
                                bucket.count > max.count ? bucket : max
                              ).range
                            : 'N/A'
                          }
                        </StatNumber>
                      </Stat>
                      
                      <Stat 
                        bg="gradient-to-br from-blue-50 to-blue-100" 
                        p={4} 
                        borderRadius="xl" 
                        shadow="md"
                        borderWidth="1px"
                        borderColor="blue.200"
                      >
                        <StatLabel fontSize="sm" color="blue.600" fontWeight="medium">
                          📈 Active Ranges
                        </StatLabel>
                        <StatNumber fontSize="lg" color="blue.700">
                          {growthHistogram.filter(bucket => bucket.count > 0).length}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                  
                  <Box>
                    <Heading size="sm" mb={4} color="orange.600">⚡ Growth Rate Statistics</Heading>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                      <Stat 
                        bg="gradient-to-br from-orange-50 to-orange-100" 
                        p={4} 
                        borderRadius="xl" 
                        shadow="md"
                        borderWidth="1px"
                        borderColor="orange.200"
                      >
                        <StatLabel fontSize="sm" color="orange.600" fontWeight="medium">
                          🎯 Most Common Range
                        </StatLabel>
                        <StatNumber fontSize="lg" color="orange.700">
                          {growthRateHistogram.length > 0 
                            ? growthRateHistogram.reduce((max, bucket) => 
                                bucket.count > max.count ? bucket : max
                              ).range
                            : 'N/A'
                          }
                        </StatNumber>
                      </Stat>
                      
                      <Stat 
                        bg="gradient-to-br from-purple-50 to-purple-100" 
                        p={4} 
                        borderRadius="xl" 
                        shadow="md"
                        borderWidth="1px"
                        borderColor="purple.200"
                      >
                        <StatLabel fontSize="sm" color="purple.600" fontWeight="medium">
                          📊 Active Ranges
                        </StatLabel>
                        <StatNumber fontSize="lg" color="purple.700">
                          {growthRateHistogram.filter(bucket => bucket.count > 0).length}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>
                </SimpleGrid>

                {/* Growth Insights Section */}
                <Box 
                  bg="gradient-to-r from-green-50 to-emerald-50" 
                  borderRadius="xl" 
                  p={6} 
                  borderWidth="1px" 
                  borderColor="green.200"
                >
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaFireAlt} color="green.500" boxSize={5} />
                    <Heading size="md" color="green.700">Growth Insights</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium" color="green.600">
                        Growth Leaders
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        {growthHistogram.length > 0 && (
                          (() => {
                            const topBuckets = growthHistogram.filter(b => b.count > 0).slice(-2);
                            const topGrowthCount = topBuckets.reduce((sum, b) => sum + b.count, 0);
                            const percentage = (topGrowthCount / businessGrowthData.length) * 100;
                            return `${percentage.toFixed(1)}% of businesses are in the top growth ranges, indicating ${percentage > 20 ? 'strong' : percentage > 10 ? 'moderate' : 'limited'} market momentum`;
                          })()
                        )}
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium" color="green.600">
                        Market Velocity
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        Average growth rate distribution shows{' '}
                        {growthRateHistogram.filter(b => b.count > 0).length > 5 
                          ? 'diverse growth patterns across the market' 
                          : 'concentrated growth patterns in specific ranges'
                        }
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </Box>
              </VStack>
            </TabPanel>

            {/* Rankings Panel */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <RankingListComponent
                    title="Top Growth Rankings"
                    data={top5Growth}
                    valueKey="totalGrowth"
                    unit=" reviews"
                    side="top"
                  />
                </Box>
                <Box>
                  <RankingListComponent
                    title="Lowest Growth Rankings"
                    data={bottom5Growth}
                    valueKey="totalGrowth"
                    unit=" reviews"
                    side="bottom"
                  />
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Details Panel */}
            <TabPanel>
              <ModernDetailsTable
                data={businessGrowthData}
                columns={columns}
                onRowClick={handleBusinessClick}
                accentColor="green"
                defaultSortColumnId={2}
                defaultSortAsc={false}
                searchPlaceholder="Search businesses..."
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Market Metrics */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <MetricCard
          icon={FaChartLine}
          title="Total Market Growth"
          value={marketMetrics.totalMarketGrowth.toLocaleString()}
          description={dateRange?.startDate && dateRange?.endDate ?
            "New reviews in selected period" :
            "New reviews in 3 months"}
          colorScheme="purple"
        />
        <MetricCard
          icon={FaUsers}
          title="Average Growth"
          value={marketMetrics.averageGrowth.toFixed(0)}
          description="Reviews per business"
          colorScheme="blue"
        />
        <MetricCard
          icon={FaFireAlt}
          title="Monthly Average"
          value={marketMetrics.avgMonthlyGrowth.toFixed(1)}
          description="New reviews per month"
          colorScheme="orange"
        />
      </SimpleGrid>
      <Box 
  bg={cardBg} 
  borderRadius="xl" 
  p={6} 
  shadow="lg" 
  borderWidth="1px" 
  borderColor={borderColor}
  mb={8}
>
  <HStack justify="space-between" align="center" mb={6}>
    <VStack align="start" spacing={1}>
      <Heading size="md" color="gray.700">📈 Growth Amount Distribution</Heading>
      <Text fontSize="sm" color="gray.500">
        Distribution of {businessGrowthData.length} businesses by 3-month growth amounts
      </Text>
    </VStack>
    <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
      {growthHistogram.filter(bucket => bucket.count > 0).length} Active Ranges
    </Badge>
  </HStack>
  
  <Box height={{ base: "250px", md: "350px" }} position="relative">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={growthHistogram}
        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
      >
        <defs>
          <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#27AE60" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#27AE60" stopOpacity={0.3}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis 
          dataKey="range" 
          angle={-45}
          textAnchor="end"
          height={60}
          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
        />
        <YAxis 
          tick={{ fill: '#2D3748', fontSize: 13, fontWeight: 600 }}
          domain={[0, (dataMax) => Math.ceil(dataMax + 10)]}
        />
        <RechartsTooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return (
                <Box
                  bg="white"
                  p={3}
                  boxShadow="lg"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  maxW="300px"
                >
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" fontSize="sm" color="green.600">
                      🚀 Range: {data.range} reviews
                    </Text>
                    <Text fontSize="sm">
                      <strong>{data.count}</strong> businesses
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {((data.count / businessGrowthData.length) * 100).toFixed(1)}% of market
                    </Text>
                  </VStack>
                </Box>
              );
            }
            return null;
          }}
        />
        <Bar 
          dataKey="count" 
          fill="url(#growthGradient)"
          cursor="pointer"
          onClick={(data) => {
            if (data && data.businesses && data.businesses.length > 0) {
              handleBusinessClick(data.businesses[0]);
            }
          }}
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  </Box>
</Box>

      {/* Review Growth Over Time - All Businesses Line Chart */}
      <Box
        bg={cardBg}
        borderRadius='xl'
        p={8}
        shadow='lg'
        borderWidth='1px'
        borderColor={borderColor}
        mb={8}
        position='relative'
        overflow='hidden'
      >
        {/* Background gradient accent */}
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          height='4px'
          bgGradient='linear(to-r, green.400, teal.500, blue.400)'
        />

        <VStack
          align='stretch'
          spacing={6}
        >
          <HStack
            justify='space-between'
            align='center'
          >
            <HStack spacing={3}>
              <Icon
                as={FaChartLine}
                color='green.500'
                boxSize={6}
              />
              <VStack
                align='start'
                spacing={0}
              >
                <Heading
                  size='lg'
                  color='gray.700'
                >
                  Review Growth Over Time
                </Heading>
                <Text
                  fontSize='sm'
                  color='gray.500'
                >
                  Monthly new reviews for all {combinedData.length} businesses in the market
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme='green'
              variant='subtle'
              fontSize='sm'
              px={3}
              py={1}
              borderRadius='full'
            >
              {allBusinessesGrowthData.length} Months
            </Badge>
          </HStack>

          <Box
            height={{ base: '350px', md: '600px' }}
            bg={statBg}
            borderRadius='lg'
            p={4}
          >
            <ResponsiveContainer
              width='100%'
              height='100%'
            >
              <LineChart
                data={allBusinessesGrowthData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid
                  strokeDasharray='5 5'
                  stroke='#CBD5E0'
                  strokeOpacity={0.8}
                />
                <XAxis
                  dataKey='displayMonth'
                  tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                />
                <YAxis
                  tick={{ fill: '#2D3748', fontSize: 14, fontWeight: 600 }}
                  axisLine={{ stroke: borderColor }}
                  tickLine={{ stroke: borderColor }}
                  label={{
                    value: 'New Reviews per Month',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: '#2D3748', fontSize: 14, fontWeight: 600 }
                  }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: cardBg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                  formatter={(value, name) => [value.toLocaleString(), name]}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: '20px',
                    maxHeight: '100px',
                    overflowY: 'auto'
                  }}
                />
                {allBusinessNames.map((businessName, index) => (
                  <Line
                    key={businessName}
                    type='monotone'
                    dataKey={businessName}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Quick Insights */}
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing={4}
          >
            <Box
              bg='green.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='green.200'
            >
              <Text
                fontSize='sm'
                color='green.600'
                fontWeight='medium'
              >
                📈 Tracking
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='green.700'
              >
                {combinedData.length} Businesses
              </Text>
              <Text
                fontSize='xs'
                color='green.500'
              >
                Monthly review growth
              </Text>
            </Box>
            <Box
              bg='teal.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='teal.200'
            >
              <Text
                fontSize='sm'
                color='teal.600'
                fontWeight='medium'
              >
                📅 Time Period
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='teal.700'
              >
                {allBusinessesGrowthData.length} Months
              </Text>
              <Text
                fontSize='xs'
                color='teal.500'
              >
                In selected date range
              </Text>
            </Box>
            <Box
              bg='blue.50'
              p={4}
              borderRadius='lg'
              borderWidth='1px'
              borderColor='blue.200'
            >
              <Text
                fontSize='sm'
                color='blue.600'
                fontWeight='medium'
              >
                🚀 Total Growth
              </Text>
              <Text
                fontSize='lg'
                fontWeight='bold'
                color='blue.700'
              >
                {marketMetrics.totalMarketGrowth.toLocaleString()}
              </Text>
              <Text
                fontSize='xs'
                color='blue.500'
              >
                New reviews in period
              </Text>
            </Box>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Add the Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        business={selectedBusiness}
        yearData={yearData}
        sortedBySentiment={sortedBySentiment}
        sortedByTotal={sortedByTotal}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
        demoMode={demoMode}
        demoTrafficHistory={demoMode ? demoData?.modalTrafficHistory : undefined}
        demoPricingData={demoMode ? demoData?.buildDemoPricing : undefined}
      />
    </Box>
  );
};

export default GrossGrowthDashboard; 